import React, { useEffect, useMemo, useState } from 'react';
import './Business.css';

import DesktopOnlyBlock from './components/DesktopOnlyBlock';
import CreateBusinessHeader from './components/CreateBusinessHeader';
import BusinessDetailsForm from './components/BusinessDetailsForm';
import CreateFooter from './components/CreateFooter';
import useHttp from '../../hooks/http.hook';
import { useNavigate, useLocation } from 'react-router-dom';
import { BusinessColorTypes } from '../../types/BusinessColorTypes';
import { businessViewById } from '../../types/BusinessViewTypes';
import { businessTypeById } from '../../types/BusinessType';

function UpdateBusiness({ initialBusiness }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { loading, requestWithMeta } = useHttp();

    const businessFromState = location?.state?.business || null;
    const initial = initialBusiness || businessFromState || null;

    const [name, setName] = useState(initial?.name ?? '');
    const [customName, setCustomName] = useState(initial?.customName ?? '');
    const [logoUrl, setLogoUrl] = useState(initial?.logoUrl ?? '');
    const [colorType, setColorType] = useState(
        Number(initial?.colorType) === BusinessColorTypes.WHITE
            ? BusinessColorTypes.WHITE
            : BusinessColorTypes.BLACK
    );
    const [viewType, setViewType] = useState(businessViewById(initial?.viewType).id);
    const [typeId, setTypeId] = useState(businessTypeById(initial?.typeId).id);
    const [error, setError] = useState(null);
    const [prefillLoading, setPrefillLoading] = useState(false);
    const [isLogoUploading, setIsLogoUploading] = useState(false);

    useEffect(() => {
        // Instant prefill from navigation state/prop (if present),
        // then we still fetch fresh data from API below.
        if (!initial) return;
        setName(initial?.name ?? '');
        setCustomName(initial?.customName ?? '');
        setLogoUrl(initial?.logoUrl ?? '');
        setColorType(
            Number(initial?.colorType) === BusinessColorTypes.WHITE
                ? BusinessColorTypes.WHITE
                : BusinessColorTypes.BLACK
        );
        setViewType(businessViewById(initial?.viewType).id);
        setTypeId(businessTypeById(initial?.typeId).id);
    }, [initial]);

    useEffect(() => {
        let cancelled = false;

        async function loadBusiness() {
            setError(null);
            setPrefillLoading(true);
            try {
                const res = await requestWithMeta('/operator/business', 'GET');
                if (cancelled) return;

                if (!res?.ok) {
                    setError(res?.data?.error || res?.data?.message || 'Не удалось загрузить бизнес');
                    return;
                }

                const business = res?.data?.business ?? res?.data ?? null;
                setName(business?.name ?? '');
                setCustomName(business?.customName ?? '');
                setLogoUrl(business?.logoUrl ?? '');
                setColorType(
                    Number(business?.colorType) === BusinessColorTypes.WHITE
                        ? BusinessColorTypes.WHITE
                        : BusinessColorTypes.BLACK
                );
                setViewType(businessViewById(business?.viewType).id);
                setTypeId(businessTypeById(business?.typeId).id);
            } finally {
                if (!cancelled) setPrefillLoading(false);
            }
        }

        loadBusiness();
        return () => {
            cancelled = true;
        };
    }, [requestWithMeta]);

    const payloadPreview = useMemo(() => {
        return {
            name: String(name ?? '').trim(),
            customName: String(customName ?? '').trim(),
            logoUrl: String(logoUrl ?? '').trim() || null,
            colorType,
            viewType,
            typeId,
        };
    }, [name, customName, logoUrl, colorType, viewType, typeId]);

    const isSaveDisabled =
        loading || isLogoUploading || prefillLoading || String(name ?? '').trim().length === 0;

    return (
        <div className="shop-create-root">
            <DesktopOnlyBlock />

            <div className="shop-create-mobile">
                <CreateBusinessHeader
                    title="Ваш бизнес"
                    subtitle="Укажите данные вашего бизнеса"
                    onBack={() => navigate('/branch/store')}
                />

                <main className="shop-create-content">
                    {error ? (
                        <section className="shop-create-section shop-create-sectionError">
                            <div className="shop-create-label">Ошибка</div>
                            <div className="shop-create-help shop-create-helpTight">
                                {error}
                            </div>
                        </section>
                    ) : null}
 
                    {prefillLoading ? (
                        <div className="shop-form-split">
                            <div className="shop-form-split-col">
                                <section className="shop-create-section">
                                    <div className="shop-create-skeleton shop-create-skeleton--text shop-create-skeletonLabel" />
                                    <div className="shop-create-skeleton shop-create-skeletonInput" />
                                </section>

                                <section className="shop-create-section">
                                    <div className="shop-create-skeleton shop-create-skeleton--text shop-create-skeletonLabel" />
                                    <div className="shop-create-skeleton shop-create-skeleton--text shop-create-skeletonHelp" />
                                    <div className="shop-create-skeleton shop-create-skeletonInput" />
                                </section>

                                <section className="shop-create-section">
                                    <div className="shop-create-skeleton shop-create-skeleton--text shop-create-skeletonLabel" />
                                    <div className="shop-create-skeleton shop-create-skeletonInput" />
                                </section>
                            </div>
                            <div className="shop-form-split-col">
                                <section className="shop-create-section">
                                    <div className="shop-create-skeleton shop-create-skeleton--text shop-create-skeletonLabel" />
                                    <div className="shop-create-skeleton shop-create-skeleton--text shop-create-skeletonHelp" />
                                    <div className="shop-create-skeleton shop-create-skeletonInput" />
                                </section>
                                <section className="shop-create-section">
                                    <div className="shop-create-skeleton shop-create-skeleton--text shop-create-skeletonLabel" />
                                    <div className="shop-create-skeleton shop-create-skeletonInput" />
                                </section>
                                <section className="shop-create-section">
                                    <div className="shop-create-skeleton shop-create-skeleton--text shop-create-skeletonLabel" />
                                    <div className="shop-create-skeleton shop-create-skeleton--text shop-create-skeletonHelp" />
                                    <div className="shop-create-skeleton shop-create-skeletonInput" />
                                </section>
                            </div>
                        </div>
                    ) : (
                        <BusinessDetailsForm
                            name={name}
                            onNameChange={setName}
                            customName={customName}
                            onCustomNameChange={setCustomName}
                            logoUrl={logoUrl}
                            onLogoUrlChange={setLogoUrl}
                            onLogoUploadLoadingChange={setIsLogoUploading}
                            colorType={colorType}
                            onColorTypeChange={setColorType}
                            viewType={viewType}
                            onViewTypeChange={setViewType}
                            typeId={typeId}
                            onTypeIdChange={setTypeId}
                            linkLoading={loading}
                        />
                    )}
                </main>

                <CreateFooter
                    disabled={isSaveDisabled}
                    label={prefillLoading ? 'Загрузка…' : loading ? 'Сохранение…' : 'Сохранить'}
                    onCreate={async () => {
                        setError(null);
                        const res = await requestWithMeta('/operator/business', 'PUT', payloadPreview);
                        if (!res?.ok) {
                            setError(res?.data?.error || res?.data?.message || 'Не удалось обновить бизнес');
                            return;
                        }
                        navigate('/branch/store', { replace: true });
                    }}
                />
            </div>
        </div>
    );
}

export default UpdateBusiness;

